import { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type Table,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { useConversations } from "../../hooks/use-conversation-data";
import { getConversationsTableColumns } from "../columns/conversations-columns";
import { useTableState, type TableStateConfig } from "./use-table-state";

export interface UseConversationsTableResult {
  table: Table<ConversationListItemResponse>;
  data: ConversationListItemResponse[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export function useConversationsTable(
  config?: TableStateConfig,
): UseConversationsTableResult {
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useConversations();

  const columns = useMemo(() => getConversationsTableColumns(), []);

  const tableState = useTableState(config);

  const table = useReactTable({
    data: data ?? [],
    columns,
    state: {
      sorting: tableState.sorting,
      columnFilters: tableState.columnFilters,
      columnVisibility: tableState.columnVisibility,
      rowSelection: tableState.rowSelection,
      globalFilter: tableState.globalFilter,
    },
    onSortingChange: tableState.setSorting,
    onColumnFiltersChange: tableState.setColumnFilters,
    onColumnVisibilityChange: tableState.setColumnVisibility,
    onRowSelectionChange: tableState.setRowSelection,
    onGlobalFilterChange: tableState.setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
    // Disable client-side pagination since we're using infinite scroll
    manualPagination: true,
  });

  return {
    table,
    data: data ?? [],
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
}
